// @flow

import _ from 'lodash';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { RestLink } from 'apollo-link-rest';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';

import { fragmentTypes } from '@zapier/toolbox-graphql-client';

import QueryCacheLink from 'app/common/apollo/QueryCacheLink';
import createLoggingLink from 'app/common/apollo/ApolloLoggingLink';
import createNetworkInterface from 'app/common/apollo/createNetworkInterface';
import fetch from 'app/common/fetch';
import typePatcher from 'app/common/apollo/typePatcher';
import { GRAPHQL_API_URL } from 'app/common/apollo/constants';
import { storeRequestHistory } from 'app/common/apollo/fetchHistoryCache';

type CreateApolloClientOptions = {
  links?: Array<ApolloLink>,
  terminatingLink?: ApolloLink,
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: fragmentTypes,
});

// This is the last link in an apollo link chain (eg. ApolloLink.from).
// It has to come last as it executes the request.
function getDefaultTerminator() {
  return createNetworkInterface({
    uri: GRAPHQL_API_URL,
    opts: {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
  });
}

function createApolloClient({
  links = [],
  terminatingLink,
}: CreateApolloClientOptions) {
  /**
   * Remove __typename from mutations. See `removeMutationTypenameLink` below.
   */
  function omitTypename(key, value) {
    return key === '__typename' ? undefined : value;
  }

  /**
   * Remove `__typename` from mutations.
   * More context about this here: https://zapier.slack.com/archives/C0466L7SX/p1548885576939400
   */
  const removeMutationTypenameLink = new ApolloLink((operation, forward) => {
    // Remove __typename from mutations
    if (operation.variables) {
      operation.variables = JSON.parse(
        JSON.stringify(operation.variables),
        omitTypename
      );
    }

    return forward(operation);
  });

  /**
   * Ensures that named queries are executed at least once per session. This
   * makes sure that the client will always have fresh data (e.g. new services)
   */
  const fetchHistoryLink = new ApolloLink((operation, forward) => {
    storeRequestHistory(operation.operationName, operation.variables);

    return forward(operation);
  });

  const queryCacheLink = new QueryCacheLink();
  const loggingLink = createLoggingLink();

  const restLink = new RestLink({
    // Use our custom `fetch` to add necessary headers like `Cookie`
    customFetch: fetch,
    uri: '/api/v3', // Default for now
    endpoints: {
      v3: '/api/v3',
      v4: '/api/v4',
      platform: '/api/platform',
    },
    typePatcher,
    // Needed so Edge doesn't blow up on `Headers.forEach` calls. See:
    // https://github.com/apollographql/apollo-link-rest/issues/93#issuecomment-404155120
    headers: {
      'Content-Type': 'application/json',
    },
    fieldNameNormalizer: _.camelCase,
    fieldNameDenormalizer: _.snakeCase,
  });

  const client = new ApolloClient({
    link: ApolloLink.from(
      _.compact([
        queryCacheLink,
        fetchHistoryLink,
        removeMutationTypenameLink,
        loggingLink,
        ..._.castArray(links),
        restLink,
        terminatingLink || getDefaultTerminator(), // always last
      ])
    ),
    cache: new InMemoryCache({
      fragmentMatcher,
    }),
    queryDeduplication: true,
    name: 'web', // identifies this client as "web" in apollo engine
  });

  return client;
}

export default createApolloClient;
